import { Fragment } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useHostingSitesV2 } from '@newfold/huapi-js';

import useAccount from '~/hooks/useAccount';
import { useBrandInfo } from '~/hooks/useBrandInfo';
import BtnGroup from '~/scenes/AddSetupSiteFlows/components/MultiStepCard/BtnGroup';
import CardHeader from '~/scenes/AddSetupSiteFlows/components/MultiStepCard/CardHeader';
import { useAddSiteContext } from '~/scenes/AddSetupSiteFlows/context/AddSiteContext';

import { defaultFormData, FormDataType } from '../..';

export type ChooseTypePropOptions = {
  formData: FormDataType;
  changeLoading?: boolean;
};

const ChooseType = ({
  formData,
  changeLoading = false,
}: ChooseTypePropOptions) => {
  const { t } = useTranslation(['addSites', 'common']);
  const { brandFromJWT: brand } = useBrandInfo();
  const { id: hostingId } = useAccount();
  const navigate = useNavigate();
  const { optionSelected, setOptionSelected } = useAddSiteContext();

  const { reset, setValue } = useFormContext();

  const { data: sitesData } = useHostingSitesV2(
    hostingId,
    {},
    {
      query: {
        enabled: !!hostingId,
        select: (data) => ({ data }),
      },
    },
  );

  // require at least 1 site to be able to clone
  const canPerformClone = +sitesData?.data?.data?.total! > 0;

  const siteAddSelection = [
    {
      id: 1,
      title: t('addSites:addSiteOptions.cloud.wordpressOption'),
      description: t('addSites:addSiteOptions.cloud.wordpressDesc'),
      testId: 'AddSite',
      subtype: 'wordpress',
      cloneWordpress: false,
      canShow: true,
      enabled: true,
      action: 'add',
      navigateTo: 'title',
    },
    {
      id: 2,
      title: t('addSites:addSiteOptions.cloud.cloneSiteOption'),
      description: `${t('addSites:addSiteOptions.cloud.cloneSiteDesc')}. ${
        !canPerformClone ? 'Install at least one site to use this option.' : ''
      }`,
      subtype: 'wordpress',
      testId: 'clone',
      cloneWordpress: true,
      canShow: true,
      enabled: canPerformClone,
      action: 'clone',
      navigateTo: 'site',
    },
    {
      id: 3,
      title: t('addSites:addSiteOptions.cloud.transferSiteTitle'),
      description: t('addSites:addSiteOptions.cloud.transferSiteDesc'),
      subtype: 'wordpress',
      testId: 'transferSite',
      cloneWordpress: false,
      canShow: brand.toLowerCase() === 'bluehost',
      enabled: true,
      action: 'transfer',
      navigateTo: 'config',
    },
  ];

  const handleContinueBtn = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!optionSelected && !!formData?.clone_site_id) {
      setValue('clone_site_id', undefined);
      setValue('title', '');
    }

    switch (optionSelected) {
      case 'add':
        return navigate('title');
      case 'clone':
        return navigate('site');
      case 'transfer':
        return navigate('config');
      default:
        return navigate('title');
    }
  };

  const siteAddSelectionList = siteAddSelection.filter((item) => item.canShow);

  const handleCancel = () => {
    reset(defaultFormData);
    navigate(-1);
  };

  return (
    <Stack spacing={2} data-testid="choose-type-step">
      <CardHeader
        headerTitle={t('addSites:addSiteOptions.title')}
        hasBackArrow={false}
      />
      <Stack spacing={4}>
        <Grid container>
          <Grid item xs={12} md={10}>
            <Stack spacing={2}>
              {siteAddSelectionList.map((optionBtn, id) => (
                <Fragment key={id}>
                  {optionBtn?.id === 3 && optionBtn?.canShow && (
                    <Typography align="center">{t('or')}</Typography>
                  )}
                  <Card
                    key={optionBtn.id}
                    variant={
                      optionBtn?.action === optionSelected
                        ? 'selected'
                        : 'outlined'
                    }
                    onClick={() => {
                      if (!optionBtn?.enabled) return;
                      setOptionSelected(optionBtn?.action);
                    }}
                    data-testid={optionBtn.testId}
                    sx={{
                      // use disabled text styling if option is not available
                      color: !optionBtn?.enabled ? 'text.disabled' : undefined,
                    }}
                  >
                    <CardActionArea disabled={!optionBtn?.enabled}>
                      <CardContent>
                        <Stack direction="row" spacing={1}>
                          <Radio
                            checked={optionBtn?.action === optionSelected}
                            disabled={!optionBtn?.enabled}
                          />
                          <Stack direction="column">
                            <Typography variant="h3">
                              {optionBtn?.title}
                            </Typography>
                            <Typography>{optionBtn?.description}</Typography>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Fragment>
              ))}
            </Stack>
          </Grid>
        </Grid>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <BtnGroup
            onCancelHandler={() => handleCancel()}
            hasSkipBtn={false}
            hasCancelBtn
            hasFinishBtn={false}
            loading={changeLoading}
            onClickHandler={handleContinueBtn}
            showInstallationModal={optionSelected !== 'transfer'}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default ChooseType;
